import type { ProjectListItem } from './projects.types';

/** B9 viewer `access_level`. Live-grant Work threads are view-only in v1. */
export const PROJECT_VIEW_ACCESS_LEVEL = 'view';

/** B9 owner/write `access_level` values. Absent/null still means owner (None-twin). */
const PROJECT_WRITABLE_ACCESS_LEVELS = new Set(['edit', 'owner', 'full']);

export type ProjectShareGating = {
  canShare: boolean;
  canFork: boolean;
  readOnly: boolean;
  isSharedWithMe: boolean;
  ownerDisplayName: string | null;
};

type ProjectShareFields = Pick<
  ProjectListItem,
  | 'can_edit'
  | 'can_share'
  | 'can_fork'
  | 'is_shared_with_me'
  | 'access_level'
  | 'owner_display_name'
>;

/**
 * Map B9 ProjectDetail/ListItem fields onto the FE gating booleans.
 *
 * Absent fields (today's owner-only API) stay writable and hide share/fork.
 * A viewer is read-only when `access_level === "view"` or `is_shared_with_me`.
 * A present-but-unrecognised `access_level` also locks (fail closed). Missing
 * or null stays writable so today's owner-only API is the None-twin.
 * `can_edit` is not used: B9 defaults it false, which would lock owners if the
 * field arrives before B3 computes the access result.
 */
export const deriveProjectShareGating = (
  project: ProjectShareFields | null | undefined
): ProjectShareGating => {
  const isSharedWithMe = project?.is_shared_with_me === true;
  const accessLevel = project?.access_level;
  const hasUnrecognisedAccessLevel =
    typeof accessLevel === 'string' &&
    accessLevel.length > 0 &&
    accessLevel !== PROJECT_VIEW_ACCESS_LEVEL &&
    !PROJECT_WRITABLE_ACCESS_LEVELS.has(accessLevel);
  const readOnly =
    accessLevel === PROJECT_VIEW_ACCESS_LEVEL ||
    isSharedWithMe ||
    hasUnrecognisedAccessLevel;

  return {
    canShare: project?.can_share === true,
    canFork: project?.can_fork === true,
    readOnly,
    isSharedWithMe,
    ownerDisplayName: project?.owner_display_name ?? null,
  };
};
